import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, setAuthToken } from '../App';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Invalid authentication response');
          navigate('/login');
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for user data via backend
        const response = await fetch(`${API}/auth/google/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Authentication failed');
        }

        const data = await response.json();
        
        // Store the JWT token
        setAuthToken(data.token);
        
        toast.success(`Welcome, ${data.user.name}!`);

        // Clear the hash from URL and navigate to dashboard
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate based on user role
        if (data.user.role === 'candidate') {
          navigate('/candidate', { replace: true });
        } else if (data.user.role === 'lead_specialist') {
          navigate('/lead-specialist', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (error) {
        console.error('Google auth error:', error);
        toast.error(error.message || 'Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processGoogleAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="text-center">
        <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800">Signing you in...</h2>
        <p className="text-slate-500 mt-2">Please wait while we complete your authentication</p>
      </div>
    </div>
  );
};

export default AuthCallback;
